import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Upload, Trash2, Edit2, Save, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageManager = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editAltText, setEditAltText] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchImages = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/images`, {
        withCredentials: true
      });
      setImages(response.data);
    } catch (error) {
      
      setMessage({ type: 'error', text: 'Afbeeldingen laden mislukt' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      setMessage({ type: 'error', text: 'Alleen JPEG, PNG en WebP bestanden zijn toegestaan' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'Bestand is te groot (max 5MB)' });
      return;
    }

    setUploading(true);
    setMessage({ type: '', text: '' });

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('alt_text', file.name.split('.')[0]);

      const response = await axios.post(
        `${API_URL}/api/images/upload`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      setImages([response.data, ...images]);
      setMessage({ type: 'success', text: 'Afbeelding succesvol geüpload!' });
      event.target.value = '';
    } catch (error) {
      console.error('Upload failed:', error);
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Upload mislukt' });
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateAltText = async (imageId) => {
    try {
      await axios.put(
        `${API_URL}/api/images/${imageId}`,
        { alt_text: editAltText },
        { withCredentials: true }
      );

      setImages(images.map(img => 
        img.id === imageId ? { ...img, alt_text: editAltText } : img
      ));
      setEditingId(null);
      setMessage({ type: 'success', text: 'Alt-tekst bijgewerkt!' });
    } catch (error) {
      console.error('Update failed:', error);
      setMessage({ type: 'error', text: 'Bijwerken mislukt' });
    }
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm('Weet je zeker dat je deze afbeelding wilt verwijderen?')) return;

    try {
      await axios.delete(`${API_URL}/api/images/${imageId}`, {
        withCredentials: true
      });
      setImages(images.filter(img => img.id !== imageId));
      setMessage({ type: 'success', text: 'Afbeelding verwijderd' });
    } catch (error) {
      console.error('Delete failed:', error);
      setMessage({ type: 'error', text: 'Verwijderen mislukt' });
    }
  };

  const copyToClipboard = (url) => {
    const fullUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    setMessage({ type: 'success', text: 'URL gekopieerd naar klembord!' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          <p className="mt-4 text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="sm">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Terug
                </Button>
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">Afbeeldingen Beheren</h1>
            </div>
            <div>
              <input
                type="file"
                id="image-upload"
                className="hidden"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleUpload}
                disabled={uploading}
              />
              <label htmlFor="image-upload">
                <Button
                  as="span"
                  disabled={uploading}
                  className="bg-blue-700 hover:bg-blue-800 cursor-pointer"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploaden...' : 'Upload Afbeelding'}
                </Button>
              </label>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <div
            className={`mb-6 p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Alle Afbeeldingen ({images.length})</CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Toegestane formaten: JPEG, PNG, WebP | Max bestandsgrootte: 5MB
            </p>
          </CardHeader>
          <CardContent>
            {images.length === 0 ? (
              <div className="text-center py-12">
                <Upload className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">Nog geen afbeeldingen geüpload</p>
                <label htmlFor="image-upload">
                  <Button as="span" className="cursor-pointer">
                    Upload je eerste afbeelding
                  </Button>
                </label>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {images.map((image) => (
                  <Card key={image.id} className="overflow-hidden">
                    <div className="aspect-video bg-gray-100 relative">
                      <img
                        src={image.url.startsWith('http') ? image.url : `${window.location.origin}${image.url}`}
                        alt={image.alt_text}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23ddd" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EAfbeelding niet beschikbaar%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        {editingId === image.id ? (
                          <div className="flex gap-2">
                            <Input
                              value={editAltText}
                              onChange={(e) => setEditAltText(e.target.value)}
                              placeholder="Alt tekst"
                              className="flex-1"
                            />
                            <Button
                              size="sm"
                              onClick={() => handleUpdateAltText(image.id)}
                            >
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingId(null)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {image.alt_text}
                            </p>
                            <p className="text-xs text-gray-500">
                              {(image.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        )}

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => copyToClipboard(image.url)}
                          >
                            Kopieer URL
                          </Button>
                          {editingId !== image.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setEditingId(image.id);
                                setEditAltText(image.alt_text);
                              }}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(image.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ImageManager;